import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route, useLocation, Link } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { Home, BookOpen, Heart, Calendar, Settings as SettingsIcon } from 'lucide-react';
import i18n from '@/lib/i18n';
import Colors from '@/constants/colors';
import { Layout } from '@/constants/layout';
import { getTranslation } from '@/constants/translations';
import { useAppStore } from '@/lib/store';
import { SyncToast } from '@/components/SyncToast';
import { useIsPWA } from '@/hooks/useIsPWA';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';

// Pages
import HomePage from '@/pages/Home';
import ArticlesPage from '@/pages/Articles';
import ArticleDetail from '@/pages/ArticleDetail';
import PlaceDetail from '@/pages/PlaceDetail';
import CategoryDetail from '@/pages/CategoryDetail';
import FavoritesPage from '@/pages/Favorites';
import SettingsPage from '@/pages/Settings';
import WidgetsSettingsPage from '@/pages/WidgetsSettings';
import HelpPage from '@/pages/Help';
import ReportIssuePage from '@/pages/ReportIssue';
import PrivacyPolicyPage from '@/pages/PrivacyPolicy';
import TermsOfServicePage from '@/pages/TermsOfService';
import ChangelogPage from '@/pages/Changelog';
import AboutUmlandPage from '@/pages/AboutUmland';
import CurrencyPage from '@/pages/Currency';
import WeatherPage from '@/pages/Weather';
import AirQualityPage from '@/pages/AirQuality';
import CalendarPage from '@/pages/Calendar';
import HolidaysPage from '@/pages/Holidays';
import EventsPage from '@/pages/Events';
import EventDetail from '@/pages/EventDetail';
import WordOfDayPage from '@/pages/WordOfDay';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 1,
    },
  },
});

// Pages that should not show navigation
const DETAIL_PAGES = ['articles/', 'categories/', 'places/', 'events/', 'currency', 'weather', 'air', 'calendar', 'holidays', 'word-of-day', 'widgets-settings', 'settings/'];
// Note: 'places/' still here because PlaceDetail route exists at /places/:id

function Navigation() {
  const location = useLocation();
  const { language } = useAppLanguageNav();
  const isPWA = useIsPWA();
  const { vibrate } = useHapticFeedback();

  // Check if current page is a detail page
  const isDetailPage = DETAIL_PAGES.some(page => location.pathname.includes(page));

  // Don't show navigation on detail pages
  if (isDetailPage) {
    return null;
  }

  const navItemsWithLabels = [
    { id: 'articles', icon: BookOpen, path: '/articles', label: getTranslation('articles', language) },
    { id: 'favorites', icon: Heart, path: '/favorites', label: getTranslation('favorites', language) },
    { id: 'home', icon: Home, path: '/', label: getTranslation('home', language) },
    { id: 'events', icon: Calendar, path: '/events', label: getTranslation('events', language) },
    { id: 'settings', icon: SettingsIcon, path: '/settings', label: getTranslation('settings', language) },
  ];

  // Dynamic styles based on PWA mode
  const navHeight = isPWA ? '70px' : '50px';
  const iconSize = isPWA ? 32 : 28;

  const handleNavClick = () => {
    vibrate('medium');
  };

  return (
    <nav style={{ ...styles.nav, minHeight: navHeight }}>
      <div style={styles.navContainer}>
        {navItemsWithLabels.map(({ id, icon: Icon, path, label }) => {
          const isActive = location.pathname === path;
          return (
            <Link
              key={id}
              to={path}
              title={label}
              aria-label={label}
              className="nav-link"
              onClick={handleNavClick}
              onTouchStart={() => {}}
              style={{
                ...styles.navItem,
                color: isActive ? Colors.primary : Colors.textSecondary,
              }}
            >
              <Icon size={iconSize} />
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

function useAppLanguageNav() {
  const language = useAppStore((state) => state.language);
  return { language };
}

function AppContent() {
  const location = useLocation();
  const isPWA = useIsPWA();
  const isDetailPage = DETAIL_PAGES.some(page => location.pathname.includes(page));

  const navHeight = isPWA ? '70px' : '50px';

  return (
    <>
      <SyncToast />
      <div style={{ ...styles.container, paddingBottom: isDetailPage ? 0 : `calc(${navHeight} + env(safe-area-inset-bottom))` }}>
        <main style={styles.main}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/articles" element={<ArticlesPage />} />
            <Route path="/articles/:id" element={<ArticleDetail />} />
            <Route path="/places/:id" element={<PlaceDetail />} />
            <Route path="/categories/:categorySlug" element={<CategoryDetail />} />
            <Route path="/favorites" element={<FavoritesPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/widgets" element={<WidgetsSettingsPage />} />
            <Route path="/settings/help" element={<HelpPage />} />
            <Route path="/settings/report-issue" element={<ReportIssuePage />} />
            <Route path="/settings/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/settings/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/settings/changelog" element={<ChangelogPage />} />
            <Route path="/settings/about" element={<AboutUmlandPage />} />
            <Route path="/widgets-settings" element={<WidgetsSettingsPage />} />
            <Route path="/currency" element={<CurrencyPage />} />
            <Route path="/weather" element={<WeatherPage />} />
            <Route path="/air" element={<AirQualityPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/holidays" element={<HolidaysPage />} />
            <Route path="/word-of-day" element={<WordOfDayPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/events/:id" element={<EventDetail />} />
            <Route path="*" element={<div style={{ padding: '20px', textAlign: 'center' }}>404 - Page not found</div>} />
          </Routes>
        </main>
        <Navigation />
      </div>
    </>
  );
}

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <Router>
          <AppContent />

          <style>{`
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
              -webkit-touch-callout: none;
              -webkit-user-select: none;
              -webkit-tap-highlight-color: transparent;
              user-select: none;
            }

            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
              background-color: ${Colors.background};
              overflow-y: scroll;
              padding: env(safe-area-inset-top, 0px) env(safe-area-inset-right, 0px) 0px env(safe-area-inset-left, 0px);
              position: fixed;
              width: 100%;
              height: 100vh;
            }

            html, body, #root {
              height: 100%;
              width: 100%;
            }

            input, textarea, button, select, a {
              -webkit-user-select: auto;
              user-select: auto;
            }

            @supports (padding: env(safe-area-inset-bottom)) {
              body {
                position: relative;
              }
            }

            .nav-link:active {
              transform: scale(0.95);
            }
          `}</style>
        </Router>
      </QueryClientProvider>
    </I18nextProvider>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100vh',
    width: '100%',
    minWidth: '320px',
    backgroundColor: Colors.background,
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    width: '100%',
  },
  nav: {
    position: 'fixed' as const,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: Colors.surface,
    borderTop: `1px solid ${Colors.border}`,
    boxShadow: Layout.shadowMedium,
    zIndex: 100,
    width: '100%',
    display: 'flex',
    alignItems: 'stretch',
    paddingBottom: 'env(safe-area-inset-bottom)',
    minHeight: '60px',
  },
  navContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    width: '100%',
    height: '100%',
    alignItems: 'center',
    boxSizing: 'border-box' as const,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    minWidth: '50px',
  } as React.CSSProperties,
};

export default App;
